import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import $ from 'jquery';
import './HomePage.css';
import relayLogo from '../../relaystream-logo.png';

// ─── Toast helper ─────────────────────────────────────────────────────────────
function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className={`rs-toast ${type}`}>{message}</div>;
}

// ─── Form view constants ───────────────────────────────────────────────────────
const VIEW_LOGIN  = 'login';
const VIEW_SIGNUP = 'signup';
const VIEW_FORGOT = 'forgot';

function HomePage({ history, location, match }) {
  const [view, setView]     = useState(VIEW_LOGIN);
  const [shaking, setShake] = useState(false);
  const [toasts, setToasts] = useState([]);

  // form field state
  const [loginEmail, setLoginEmail]       = useState('');
  const [loginPass, setLoginPass]         = useState('');
  const [signupEmail, setSignupEmail]     = useState('');
  const [signupPass, setSignupPass]       = useState('');
  const [forgotEmail, setForgotEmail]     = useState('');
  const [resetPass, setResetPass]         = useState('');
  const [resetConfirm, setResetConfirm]   = useState('');

  const toastId = useRef(0);

  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, msg, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const shake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  // ── Email verification via URL token ──────────────────────────────────────
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/verify/email') && match.params.token) {
      $.ajax({
        url: `/api/v1/email/verify/${match.params.token}`,
        type: 'PUT',
        success(res) {
          if (res.status === 200) {
            localStorage.setItem('isEmailVerified', true);
            addToast(res.message, 'success');
          }
        },
        error() {}
      });
    }
  }, []);

  // ── Route-based initial view ──────────────────────────────────────────────
  useEffect(() => {
    const path = location.pathname;
    if (path === '/signup') setView(VIEW_SIGNUP);
    else if (path === '/forgot-password') setView(VIEW_FORGOT);
    else if (path.includes('/reset/password')) setView('reset');
    else setView(VIEW_LOGIN);
  }, [location.pathname]);

  const isAuthenticated = !!localStorage.getItem('authToken');

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleLogin = (e) => {
    e.preventDefault();
    $.ajax({
      url: '/api/v1/normal/login',
      type: 'POST',
      data: JSON.stringify({ email: loginEmail, password: loginPass }),
      success(res) {
        const obj = res.response;
        for (let key in obj) {
          if (obj.hasOwnProperty(key)) localStorage.setItem(key, obj[key]);
        }
        history.push('/dashboard');
      },
      error(err) {
        shake();
        if (err && err.responseJSON) addToast(err.responseJSON.message, 'error');
      }
    });
  };

  const handleSignup = (e) => {
    e.preventDefault();
    $.ajax({
      url: '/api/v1/normal/signup',
      type: 'POST',
      data: JSON.stringify({ email: signupEmail, password: signupPass }),
      success(res) {
        addToast(res.message || 'Conta criada com sucesso', 'success');
        setView(VIEW_LOGIN);
      },
      error(err) {
        shake();
        if (err && err.responseJSON) addToast(err.responseJSON.message, 'error');
      }
    });
  };

  const handleForgot = (e) => {
    e.preventDefault();
    $.ajax({
      url: '/api/v1/forgot/password',
      type: 'POST',
      data: JSON.stringify({ email: forgotEmail }),
      success(res) {
        addToast(res.message || 'Link de recuperação enviado', 'info');
        setView(VIEW_LOGIN);
      },
      error(err) {
        if (err && err.responseJSON) addToast(err.responseJSON.message, 'error');
      }
    });
  };

  const handleReset = (e) => {
    e.preventDefault();
    if (resetPass !== resetConfirm) {
      addToast('As senhas não coincidem', 'error');
      return;
    }
    $.ajax({
      url: `/api/v1/reset/password/${match.params.token}`,
      type: 'PUT',
      data: JSON.stringify({ password: resetPass }),
      success(res) {
        addToast(res.message || 'Senha redefinida', 'success');
        history.push('/');
      },
      error(err) {
        if (err && err.responseJSON) addToast(err.responseJSON.message, 'error');
      }
    });
  };

  // ── Title / subtitle helpers ──────────────────────────────────────────────
  const titles = {
    [VIEW_LOGIN]: { h1: 'Entrar', p: 'Acesse sua conta' },
    [VIEW_SIGNUP]: { h1: 'Criar conta', p: 'Cadastro rápido' },
    [VIEW_FORGOT]: { h1: 'Recuperar senha', p: 'Enviaremos um link' },
    reset: { h1: 'Nova senha', p: 'Digite sua nova senha' },
  };
  const { h1, p } = titles[view] || titles[VIEW_LOGIN];

  const footerText = {
    [VIEW_LOGIN]:  'Não tem conta? Criar agora',
    [VIEW_SIGNUP]: 'Já tem conta? Entrar',
    [VIEW_FORGOT]: 'Voltar para login',
    reset:         'Voltar para login',
  }[view];

  const toggleView = () => {
    if (view === VIEW_LOGIN)  setView(VIEW_SIGNUP);
    else                      setView(VIEW_LOGIN);
  };

  if (isAuthenticated && !location.pathname.includes('/verify')) {
    return <Redirect to="/dashboard/main" />;
  }

  return (
    <div className="rs-auth-page">
      {/* Toasts */}
      <div className="rs-toast-container">
        {toasts.map(t => (
          <Toast key={t.id} message={t.msg} type={t.type} onDone={() => removeToast(t.id)} />
        ))}
      </div>

      <div className={`rs-auth-card${shaking ? ' shake' : ''}`}>
        {/* Logo */}
        <div className="rs-auth-logo">
          <img src={relayLogo} alt="Relay Stream" />
          <span>Streaming Relay Dashboard</span>
        </div>

        {/* Header */}
        <div className="rs-auth-header">
          <h1>{h1}</h1>
          <p>{p}</p>
        </div>

        {/* ── Login form ── */}
        {view === VIEW_LOGIN && (
          <form className="rs-auth-form" onSubmit={handleLogin}>
            <div className="rs-field">
              <label>Email</label>
              <input type="email" required value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)} />
            </div>
            <div className="rs-field">
              <label>Senha</label>
              <input type="password" required value={loginPass}
                onChange={e => setLoginPass(e.target.value)} />
            </div>
            <div className="rs-auth-actions">
              <span onClick={() => setView(VIEW_FORGOT)}>Esqueci minha senha</span>
            </div>
            <button type="submit" className="rs-auth-submit">Entrar</button>
          </form>
        )}

        {/* ── Signup form ── */}
        {view === VIEW_SIGNUP && (
          <form className="rs-auth-form" onSubmit={handleSignup}>
            <div className="rs-field">
              <label>Email</label>
              <input type="email" required value={signupEmail}
                onChange={e => setSignupEmail(e.target.value)} />
            </div>
            <div className="rs-field">
              <label>Senha</label>
              <input type="password" required value={signupPass}
                onChange={e => setSignupPass(e.target.value)} />
            </div>
            <button type="submit" className="rs-auth-submit">Criar conta</button>
          </form>
        )}

        {/* ── Forgot password form ── */}
        {view === VIEW_FORGOT && (
          <form className="rs-auth-form" onSubmit={handleForgot}>
            <div className="rs-field">
              <label>Email</label>
              <input type="email" required value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)} />
            </div>
            <button type="submit" className="rs-auth-submit">Enviar link</button>
          </form>
        )}

        {/* ── Reset password form ── */}
        {view === 'reset' && (
          <form className="rs-auth-form" onSubmit={handleReset}>
            <div className="rs-field">
              <label>Nova senha</label>
              <input type="password" required value={resetPass}
                onChange={e => setResetPass(e.target.value)} />
            </div>
            <div className="rs-field">
              <label>Confirmar senha</label>
              <input type="password" required value={resetConfirm}
                onChange={e => setResetConfirm(e.target.value)} />
            </div>
            <button type="submit" className="rs-auth-submit">Redefinir senha</button>
          </form>
        )}

        {/* Footer toggle */}
        <div className="rs-auth-footer">
          <span onClick={toggleView}>{footerText}</span>
        </div>
      </div>
    </div>
  );
}

export default withRouter(HomePage);



// WEBPACK FOOTER //
// ./src/components/HomePage/HomePage.js